'use client'

import { useVisibleProductCards } from '@/feature/product/hooks/useVisibleProductCards'
import { CSSSlider } from '@/ui/layouts/css-slider/CSSSlider.client'
import { ProductType, type ProductCardFragment } from '@gql-generated/graphql'
import { useEffect, useRef } from 'react'
import { useProductsListFavoriteStatus } from '../../hooks/useProductsListFavoriteStatus'
import { ProductCard } from '../product-card/ProductCard.client'

export type ProductSliderProps = {
  /* A list of products */
  products: ProductCardFragment[]

  /** Additional classes to add to the component */
  className?: string

  /** The source of the product slider */
  source?: string

  /** Indicates if the AddProductToFavoriteButton is displayed */
  isAddableToFavorites?: boolean
}

export function ProductSlider({
  products,
  className,
  source,
  isAddableToFavorites,
}: ProductSliderProps): React.ReactElement {
  const productCardsRefs = useRef<React.RefObject<HTMLDivElement | null>[]>([])

  const { setProductCardsRefs, productCardsRefs: productCardsRefsHook } = useVisibleProductCards({
    itemListName: source,
    products,
  })

  const { data: favoriteMap } = useProductsListFavoriteStatus(
    products
      .filter((product) => product.type === ProductType.Article)
      .map((product) => product.reference),
  )

  useEffect(() => {
    productCardsRefs.current = products.map(
      (_, index) => productCardsRefs.current[index] || { current: null },
    )
  }, [products])

  useEffect(() => {
    if (
      productCardsRefs.current.length > 0 &&
      productCardsRefsHook.length !== productCardsRefs.current.length
    ) {
      setProductCardsRefs(productCardsRefs.current)
    }
  }, [products, setProductCardsRefs, productCardsRefsHook.length])

  return (
    <div className={className}>
      <CSSSlider gap="md">
        {products.map(
          (product, index) =>
            product.urlKey && (
              <div key={product.urlKey} className="w-[245px]">
                <ProductCard
                  ref={productCardsRefs.current[index]}
                  isAddableToFavorites={isAddableToFavorites}
                  isFavorite={favoriteMap?.[product.reference]}
                  product={product}
                  restreintWidth
                  position={index + 1}
                  source={source}
                />
              </div>
            ),
        )}
      </CSSSlider>
    </div>
  )
}
